import { useState } from 'react';
import type { CoreOrganization, CoreRole } from '@/lib/zifekCore';
import { createCoreRole, setRolePermissions } from '@/lib/zifekCore';
import { ALL_PERMISSIONS, PERMISSION_GROUPS, roleMeta } from '@/lib/corePermissions';
import Modal from './Modal';

interface Props {
  org: CoreOrganization;
  roles: CoreRole[];
  onChanged: () => void;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function RolesManager({ org, roles, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [editRole, setEditRole] = useState<CoreRole | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openCreate = () => {
    setEditRole(null);
    setName('');
    setDescription('');
    setSelected(['dashboard.view']);
    setError('');
    setOpen(true);
  };

  const openEdit = (r: CoreRole) => {
    setEditRole(r);
    setName(r.name);
    setDescription(r.description || '');
    setSelected(r.permissions || []);
    setError('');
    setOpen(true);
  };

  const togglePerm = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleGroup = (groupKey: string) => {
    const group = PERMISSION_GROUPS.find((g) => g.key === groupKey);
    if (!group) return;
    const keys = group.permissions.map((p) => p.key);
    const allSelected = keys.every((k) => selected.includes(k));
    setSelected((prev) => (allSelected ? prev.filter((k) => !keys.includes(k)) : Array.from(new Set([...prev, ...keys]))));
  };

  const save = async () => {
    if (!name.trim()) { setError('Le nom du rôle est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      if (editRole) {
        await setRolePermissions(editRole.id, selected);
      } else {
        await createCoreRole({
          tenantId: org.tenant_id,
          organizationId: org.id,
          name: name.trim(),
          description: description.trim() || null,
          permissions: selected,
        });
      }
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const customRoles = roles.filter((r) => !r.is_system);
  const systemRoles = roles.filter((r) => r.is_system);

  const renderRoleCard = (r: CoreRole) => {
    const meta = roleMeta(r.role_key, r.name);
    const perms = r.permissions || [];
    return (
      <div key={r.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.className}`}>
              <i className={`${meta.icon} text-lg`}></i>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground-950 truncate">{r.name}</div>
              <div className="text-[11px] text-foreground-400">{r.is_system ? 'Rôle système' : 'Rôle personnalisé'}</div>
            </div>
          </div>
          {!r.is_system && (
            <button
              onClick={() => openEdit(r)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-edit-line"></i> Permissions
            </button>
          )}
        </div>
        {r.description && <p className="text-xs text-foreground-500 mb-3 leading-relaxed">{r.description}</p>}
        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-background-200/60 mt-auto">
          {perms.includes('*') ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">Toutes les permissions</span>
          ) : perms.length === 0 ? (
            <span className="text-[10px] text-foreground-400">Aucune permission</span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium">{perms.length} permission(s)</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Les rôles définissent ce que chaque membre peut faire. Créez vos propres rôles pour votre organisation.
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nouveau rôle
        </button>
      </div>

      {customRoles.length > 0 && (
        <>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">Rôles personnalisés</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">{customRoles.map(renderRoleCard)}</div>
        </>
      )}

      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">Rôles système</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{systemRoles.map(renderRoleCard)}</div>

      {open && (
        <Modal
          title={editRole ? `Permissions — ${editRole.name}` : 'Nouveau rôle'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
          <div className="space-y-3">
            {!editRole && (
              <>
                <label className="block">
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Nom du rôle *</span>
                  <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Chef d’équipe" />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Description</span>
                  <input className={INPUT} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="À quoi sert ce rôle ?" />
                </label>
              </>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className="block text-xs font-medium text-foreground-600">Permissions ({selected.length}/{ALL_PERMISSIONS.length})</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelected(ALL_PERMISSIONS)} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">Tout</button>
                <button onClick={() => setSelected([])} className="text-xs text-foreground-500 hover:text-foreground-700 font-medium cursor-pointer whitespace-nowrap">Aucun</button>
              </div>
            </div>

            <div className="space-y-3">
              {PERMISSION_GROUPS.map((g) => {
                const keys = g.permissions.map((p) => p.key);
                const allSelected = keys.every((k) => selected.includes(k));
                return (
                  <div key={g.key} className="border border-background-200/70 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(g.key)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-background-100 hover:bg-background-200/60 transition-colors cursor-pointer"
                    >
                      <span className="flex items-center gap-2 text-xs font-semibold text-foreground-800">
                        <i className={`${g.icon} text-foreground-500`}></i>
                        {g.label}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${allSelected ? 'bg-primary-100 text-primary-900' : 'bg-background-200/70 text-foreground-500'}`}>
                        {allSelected ? 'Tout' : 'Partiel'}
                      </span>
                    </button>
                    <div className="divide-y divide-background-200/50">
                      {g.permissions.map((p) => (
                        <label key={p.key} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-background-100 transition-colors">
                          <input
                            type="checkbox"
                            checked={selected.includes(p.key)}
                            onChange={() => togglePerm(p.key)}
                            className="w-4 h-4 rounded accent-primary-500"
                          />
                          <span className="text-sm text-foreground-700">{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}